import React, { useState, useMemo } from 'react';
import { Search, Check, ChevronLeft, ChevronRight, ShieldCheck } from 'lucide-react';

/**
 * FrameworkPicker — a professional regulatory-framework selector that replaces the
 * old flat <select> in the policy wizard's Source Selection step. Organizes the
 * catalogue by sector tabs, supports search, and paginates the results as cards.
 *
 * @param {Array}    props.frameworks  [{ id, name, version, controlCount, sector, region }]
 * @param {string}   props.value       selected framework id
 * @param {Function} props.onChange    (id, name) => void
 * @param {boolean}  [props.loading]
 * @param {number}   [props.pageSize]  cards per page (default 9)
 */
const SECTOR_LABELS = {
  FINANCE: 'Financial', INSURANCE: 'Insurance', HEALTHCARE: 'Healthcare',
  AI_SPECIFIC: 'AI Governance', SECURITY: 'Security', GENERAL: 'General',
  EMPLOYMENT: 'Employment', PRIVACY: 'Privacy',
};
const humanizeSector = (s) => SECTOR_LABELS[s]
  || (s ? s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Other');

const C = {
  ink: '#1c1917', sub: '#57534e', muted: '#a8a29e', border: '#e7e5e4',
  borderStrong: '#d6d3d1', accent: '#0a766b', accentSoft: '#e3f4f1', surface: '#fff', track: '#f5f5f4',
};

export default function FrameworkPicker({ frameworks = [], value, onChange, loading = false, pageSize = 9 }) {
  const [activeTab, setActiveTab] = useState('ALL');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);

  // Sector tabs (only those present), with counts.
  const tabs = useMemo(() => {
    const counts = {};
    for (const fw of frameworks) {
      const s = fw.sector || 'OTHER';
      counts[s] = (counts[s] || 0) + 1;
    }
    const sectors = Object.keys(counts).sort((a, b) => humanizeSector(a).localeCompare(humanizeSector(b)));
    return [{ key: 'ALL', label: 'All', count: frameworks.length },
      ...sectors.map((s) => ({ key: s, label: humanizeSector(s), count: counts[s] }))];
  }, [frameworks]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return frameworks.filter((fw) => {
      if (activeTab !== 'ALL' && (fw.sector || 'OTHER') !== activeTab) return false;
      if (q && !(`${fw.name} ${fw.version || ''}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [frameworks, activeTab, query]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const visible = filtered.slice(safePage * pageSize, safePage * pageSize + pageSize);

  const reset = (fn) => { fn(); setPage(0); };

  if (loading) {
    return <div style={{ fontSize: 13, color: C.muted, padding: '16px 0' }}>Loading frameworks…</div>;
  }

  return (
    <div>
      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 10 }}>
        <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: C.muted }} />
        <input
          value={query}
          onChange={(e) => reset(() => setQuery(e.target.value))}
          placeholder="Search frameworks…"
          style={{
            width: '100%', boxSizing: 'border-box', padding: '8px 10px 8px 32px',
            border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, color: C.ink, background: C.surface,
          }}
        />
      </div>

      {/* Sector tabs */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {tabs.map((t) => {
          const on = activeTab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => reset(() => setActiveTab(t.key))}
              style={{
                padding: '5px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                border: `1px solid ${on ? C.accent : C.border}`,
                background: on ? C.accentSoft : C.surface,
                color: on ? C.accent : C.sub,
              }}
            >
              {t.label}
              <span style={{ marginLeft: 6, fontWeight: 500, color: on ? C.accent : C.muted }}>{t.count}</span>
            </button>
          );
        })}
      </div>

      {/* Cards */}
      {visible.length === 0 ? (
        <div style={{ fontSize: 13, color: C.muted, padding: '20px 0', textAlign: 'center' }}>
          No frameworks match “{query}”.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
          {visible.map((fw) => {
            const selected = fw.id === value;
            return (
              <button
                key={fw.id}
                type="button"
                onClick={() => onChange?.(selected ? null : fw.id, selected ? null : fw.name)}
                style={{
                  textAlign: 'left', padding: '12px 12px', borderRadius: 10, cursor: 'pointer', position: 'relative',
                  border: `1.5px solid ${selected ? C.accent : C.border}`,
                  background: selected ? C.accentSoft : C.surface,
                  transition: 'border-color .12s, background .12s',
                }}
              >
                {selected && (
                  <span style={{
                    position: 'absolute', top: 10, right: 10, width: 18, height: 18, borderRadius: 999,
                    background: C.accent, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Check size={12} color="#fff" />
                  </span>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <ShieldCheck size={14} color={C.accent} />
                  <span style={{
                    fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
                    color: C.sub, background: C.track, padding: '2px 7px', borderRadius: 6,
                  }}>
                    {humanizeSector(fw.sector)}
                  </span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 650, color: C.ink, lineHeight: 1.3, paddingRight: 20 }}>
                  {fw.name}
                </div>
                <div style={{ fontSize: 11.5, color: C.muted, marginTop: 4 }}>
                  {fw.version ? `v${fw.version}` : ''}{fw.version && fw.controlCount != null ? ' · ' : ''}
                  {fw.controlCount != null ? `${fw.controlCount} controls` : ''}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pageCount > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
          <span style={{ fontSize: 12, color: C.muted }}>
            {filtered.length} framework{filtered.length === 1 ? '' : 's'} · page {safePage + 1} of {pageCount}
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" disabled={safePage === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 3, padding: '5px 9px', borderRadius: 7,
                border: `1px solid ${C.border}`, background: C.surface, fontSize: 12, fontWeight: 600,
                color: safePage === 0 ? C.muted : C.sub, cursor: safePage === 0 ? 'not-allowed' : 'pointer',
              }}>
              <ChevronLeft size={13} /> Prev
            </button>
            <button type="button" disabled={safePage >= pageCount - 1} onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 3, padding: '5px 9px', borderRadius: 7,
                border: `1px solid ${C.border}`, background: C.surface, fontSize: 12, fontWeight: 600,
                color: safePage >= pageCount - 1 ? C.muted : C.sub, cursor: safePage >= pageCount - 1 ? 'not-allowed' : 'pointer',
              }}>
              Next <ChevronRight size={13} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
